import React, { useEffect, useState } from "react";

import { Link, useNavigate, useLocation } from "react-router-dom";
import { buildGoogleAuthUrl, isGoogleAuthConfigured } from "@/lib/googleAuth";

import { useAuth, formatApiError } from "@/contexts/AuthContext";

import { langClass } from "@/i18n/langUtils";
import { useT } from "@/i18n/LanguageContext";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { Mail, KeyRound, Loader2, ShieldCheck } from "lucide-react";



const Login = () => {

  const { t, lang } = useT();

  const auth = t.auth || {};

  const { login } = useAuth();

  const nav = useNavigate();

  const loc = useLocation();

  const from = loc.state?.from || "/dashboard";

  const [googleLoginUrl, setGoogleLoginUrl] = useState(() => buildGoogleAuthUrl());
  const langCls = langClass(lang);

  useEffect(() => {
    setGoogleLoginUrl(buildGoogleAuthUrl());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("error") === "google") {
      const redirectUri = `${window.location.origin}/auth/callback`;
      setError(
        `Google sign-in failed. In Google Cloud Console → Credentials → OAuth client ` +
          `747142569532-…, add Authorized redirect URI exactly: ${redirectUri} ` +
          `(and JavaScript origin ${window.location.origin}). See docs/GOOGLE_OAUTH.md.`,
      );
    }
  }, [loc.search]);



  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");

  const [error, setError] = useState("");

  const [totpCode, setTotpCode] = useState("");

  const [needs2fa, setNeeds2fa] = useState(false);

  const [loading, setLoading] = useState(false);



  const submit = async (e) => {

    e.preventDefault();

    setError("");

    setLoading(true);

    try {

      await login(email.trim(), password, needs2fa ? totpCode : null);

      nav(from, { replace: true });

    } catch (err) {

      const msg = formatApiError(err);

      if (msg.toLowerCase().includes("2fa")) setNeeds2fa(true);

      setError(msg);

    } finally {

      setLoading(false);

    }

  };



  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";



  return (

    <div className="mx-auto max-w-md px-4 py-16 md:py-24" data-testid="login-page">

      <div className="text-center">

        <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]/30 mb-4">

          <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />

        </div>

        <h1 className={`font-display text-3xl font-black ${langCls}`}>{auth.welcomeBack}</h1>

        <p className={`mt-2 text-sm text-white/60 ${langCls}`}>{auth.signInSubtitle}</p>

      </div>



      {googleLoginUrl && isGoogleAuthConfigured() ? (
        <a href={googleLoginUrl} className="block mt-8" data-testid="google-login-btn">

          <Button type="button" variant="outline" className="w-full h-11 border-white/20 hover:bg-white/5 gap-3">

            <svg viewBox="0 0 24 24" className="h-4 w-4">

              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>

              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>

              <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>

              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>

            </svg>

            {auth.continueGoogle}

          </Button>

        </a>
      ) : (
        <p className="mt-8 text-xs text-amber-200/90 text-center border border-amber-500/30 rounded-lg px-3 py-2 bg-amber-500/10">
          Google sign-in is not configured in this deployment. Push the latest <code className="text-amber-100">main</code>{" "}
          branch and wait for Cloudflare to finish building, or check{" "}
          <a href="/config.js" className="text-amber-100 underline">/config.js</a> for a non-empty{" "}
          <code className="text-amber-100">googleClientId</code>.
        </p>
      )}

      <div className="my-6 flex items-center gap-3 text-xs text-white/40 font-mono-alt uppercase tracking-widest">

        <span className="flex-1 h-px bg-white/10" /> {auth.orEmail} <span className="flex-1 h-px bg-white/10" />

      </div>



      <form onSubmit={submit} className="surface-card p-6 space-y-4" data-testid="login-form">

        <div>

          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{auth.email}</Label>

          <div className="relative mt-2">

            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />

            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className={`pl-10 ${fieldCls}`} data-testid="login-email" placeholder="you@example.com" />

          </div>

        </div>

        <div>

          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{auth.password}</Label>

          <div className="relative mt-2">

            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />

            <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className={`pl-10 ${fieldCls}`} data-testid="login-password" />

          </div>

        </div>

        {needs2fa && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">Authenticator code</Label>
            <Input value={totpCode} onChange={(e) => setTotpCode(e.target.value)} className={`mt-2 ${fieldCls}`} placeholder="6-digit code" inputMode="numeric" />
          </div>
        )}

        <p className="text-right text-xs">
          <Link to="/forgot-password" className="text-[#D4AF37] hover:underline">Forgot password?</Link>
        </p>

        {error && <div className="text-xs text-[#FF6A60]" data-testid="login-error">{error}</div>}

        <Button type="submit" disabled={loading} className="w-full h-11 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="login-submit">

          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : auth.signIn}

        </Button>

      </form>



      <div className={`mt-6 text-center text-sm text-white/60 ${langCls}`}>

        {auth.newHere}{" "}

        <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="goto-register">{auth.createAccount}</Link>

      </div>

    </div>

  );

};



export default Login;

